import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Heart, Bath, Cat, Plus, Gift, X, Check, Calendar, ArrowUpRight } from 'lucide-react';
import { SERVICES } from '../../lib/data';
import Reveal from '../ui/Reveal';

const ICONS = { Sparkles, Heart, Bath, Cat, Plus, Gift };

function ServiceCard({ svc, onOpen, index }) {
  const Icon = ICONS[svc.icon] || Sparkles;
  return (
    <Reveal delay={index * 0.06}>
      {/* gradient border via padded wrapper */}
      <motion.button
        onClick={() => onOpen(svc)}
        whileHover={{ y: -8 }}
        transition={{ type: 'spring', stiffness: 300, damping: 22 }}
        className="group w-full text-left rounded-3xl p-[1.5px] bg-gradient-to-br from-white/15 via-white/5 to-white/15 hover:from-pink-400 hover:via-purple-400 hover:to-cyan-400 transition-colors duration-500 h-full"
      >
        <div className="rounded-[calc(1.5rem-1.5px)] bg-ink-800/90 backdrop-blur p-7 h-full flex flex-col">
          <div className="flex items-start justify-between">
            <div className={`inline-flex p-3.5 rounded-2xl bg-gradient-to-br ${svc.gradient} shadow-lg`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <span className="font-display text-4xl font-black text-white/10 group-hover:text-white/25 transition-colors leading-none">
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>
          <h3 className="mt-5 font-display text-2xl font-black text-white">{svc.name}</h3>
          <p className="mt-2 text-sm text-white/60 leading-relaxed grow">{svc.blurb}</p>
          <ul className="mt-5 space-y-2">
            {svc.includes.slice(0, 3).map((inc) => (
              <li key={inc} className="flex items-start space-x-2 text-[13px] text-white/50">
                <Check className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
                <span>{inc}</span>
              </li>
            ))}
          </ul>
          <span className="mt-6 inline-flex items-center space-x-1.5 text-sm font-extrabold text-pink-400 group-hover:text-white transition-colors">
            <span>Everything included</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </span>
        </div>
      </motion.button>
    </Reveal>
  );
}

export default function Services() {
  const [active, setActive] = useState(null);
  const ActiveIcon = active ? ICONS[active.icon] || Sparkles : null;

  return (
    <section id="services" className="relative py-20 md:py-28 bg-ink-900 grain overflow-hidden">
      {/* ambient glows */}
      <div aria-hidden className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-pink-500/10 blur-3xl" />
      <div aria-hidden className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
      <p aria-hidden className="pointer-events-none select-none absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap font-display font-black text-outline-light text-[20vw] leading-none">
        pamper
      </p>

      <div className="relative max-w-7xl mx-auto px-4">
        <Reveal className="max-w-3xl">
          <p className="text-xs md:text-sm font-extrabold uppercase tracking-[0.22em] text-pink-400">Our services</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.02] tracking-tight">
            Pampering for
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent"> every pet.</span>
          </h2>
          <p className="mt-4 text-lg text-white/60 max-w-xl">
            Full spa transformations to quick refreshes — dogs and cats, all breeds, all temperaments.
          </p>
        </Reveal>

        <div className="mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {SERVICES.map((svc, i) => (
            <ServiceCard key={svc.key} svc={svc} index={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink-900/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl"
            >
              <div className={`relative p-7 bg-gradient-to-br ${active.gradient}`}>
                <button
                  onClick={() => setActive(null)}
                  aria-label="Close"
                  className="absolute top-4 right-4 bg-white/25 hover:bg-white/40 rounded-full p-2 text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <ActiveIcon className="w-8 h-8 text-white/90" />
                <h3 className="mt-2 font-display text-3xl font-black text-white">{active.name}</h3>
                <p className="mt-1 text-white/85 text-sm">{active.blurb}</p>
              </div>
              <div className="p-7">
                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gray-400 mb-4">What's included</p>
                <ul className="space-y-2.5">
                  {active.includes.map((inc) => (
                    <li key={inc} className="flex items-start space-x-2.5 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{inc}</span>
                    </li>
                  ))}
                </ul>
                {active.note && (
                  <p className="mt-5 text-xs font-bold text-purple-700 bg-purple-50 rounded-xl px-4 py-3">{active.note}</p>
                )}
                <Link
                  to="/booking"
                  className="btn-shine mt-7 w-full inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-6 py-4 rounded-full font-extrabold shadow-lg transition-transform duration-300 hover:scale-[1.02]"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Book {active.name}</span>
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
